/* eslint-disable complexity */
/* eslint-disable max-statements */
/**
 * @file 知识库导入文件通用逻辑
 */
import {v1 as uuidv1} from 'uuid';

import sdk from '@/sdk';
import {WizKnownError} from '@/sdk/common/error';
import * as store from '@/store';
import type {FileItem, ClientTransformFileData, UploadInputOptions} from '@/types/document/file';
import {
    FileStatus,
    FileType,
    UPLOAD_MAXSZIE,
    FILE_MAX_TIP_MAP,
    EXCEL_UPLOAD_ERROR_MAP,
    EXCEL_DST_DEFAULT_ERROR_CODE,
    EXCEL_DST_UPLOAD_ERROR_MAP,
} from '@/constants/document/file';
import {refreshRecentList} from '@/domains/user/UserHome/common/events';
import toast, {ToastType} from '@/components/Toast';
import {createUploadFileScript} from '@/utils/document/file';
import {ServerConfig} from '@/config/routers';
import {getNodeById, updateChildNodes} from '@/domains/space/Repository/SidebarNew/sidebarTreeUtils';

const GetProgressTimeSpace = 1500; // 轮询文件导入进度时间间隔
const fileQueue: FileItem[] = []; // 文件队列
let timer = -1;

interface BosConfig {
    endpoint: string;
    sessionToken: string;
    credentials: Credentials;
}

export type IUploadMultipleFile = (
    file: File,
    bosConfig: BosConfig,
    bucket: string,
    setProgress: (progress: number) => void,
    setFileName?: () => string,
    isCache?: boolean,
) => any;

export type IGetMd5ByFile = (file: File) => Promise<string>;

/**
 * 动态获取分片上传方法
 */
export const getUploadPartialFileFunc = async () => {
    if (!(window as any).uploadMultipleFile) {
        await createUploadFileScript();
    }
    const uploadMultipleFile: IUploadMultipleFile = (window as any).uploadMultipleFile;
    if (typeof uploadMultipleFile !== 'function') {
        throw Error('bos uploadMultipleFile加载失败');
    }

    const getMd5ByFile: IGetMd5ByFile = (window as any).getMd5ByFile;
    if (typeof getMd5ByFile !== 'function') {
        throw Error('bos getMd5ByFile加载失败');
    }

    return {
        uploadPartialFile: uploadMultipleFile,
        getMd5ByFile,
    };
};

export const useUploadFile = () => {
    /**
     * 检查文件队列是否为空，若不为空则上传队头文件
     */
    const checkFileQueue = () => {
        if (fileQueue.length) {
            // 这里可以保证该函数调用前 importQueueHeadFile 已声明
            // eslint-disable-next-line no-use-before-define,@typescript-eslint/no-use-before-define
            importQueueHeadFile();
        }
    };

    const clearTimer = () => {
        timer && clearInterval(timer);
        timer = -1;
    };

    /**
     * 文件上传/导入，统一错误处理
     * @param file 文件信息
     * @param error Error 对象
     */
    const handleFileError = (file: FileItem, error: any) => {
        let defaultErrorMsg = '未知原因，导入失败';

        // 各类型默认错误文案
        switch (file.fileType) {
            case FileType.Excel:
                defaultErrorMsg = '表格导入失败，请尝试拆分表格或稍后重试';
                break;
            case FileType.PPT:
            case FileType.PDF:
                defaultErrorMsg = '导入失败，请检查文件是否已经损坏';
                break;
            case FileType.Word:
                defaultErrorMsg = '导入失败，请检查文件是否超过50M或已经损坏';
                break;
            case FileType.ExcelDst:
                defaultErrorMsg = '表格导入失败，请尝试拆分表格或稍后重试';
                break;
            default:
                break;
        }

        file.status = FileStatus.Error;
        if (error instanceof WizKnownError) {
            const {returnCode, returnMessage} = error.toResult();

            // 自定义错误文案
            switch (file.fileType) {
                case FileType.Excel:
                    defaultErrorMsg =
                        EXCEL_UPLOAD_ERROR_MAP[returnCode as keyof typeof EXCEL_UPLOAD_ERROR_MAP] || defaultErrorMsg;
                    break;
                case FileType.PPT:
                case FileType.PDF:
                case FileType.Word:
                case FileType.ExcelDst: {
                    if (returnCode === EXCEL_DST_DEFAULT_ERROR_CODE) {
                        defaultErrorMsg =
                            EXCEL_DST_UPLOAD_ERROR_MAP[returnMessage as keyof typeof EXCEL_DST_UPLOAD_ERROR_MAP]
                            || defaultErrorMsg;
                        break;
                    }
                }
            }

            file.errorMsg = returnCode === 'WizErrorNetwork' ? '网络请求出错，请稍后重试' : defaultErrorMsg;
        } else {
            file.errorMsg = defaultErrorMsg;
        }
        store.setImportFile(file);

        // 清除队列头，检查是否还有未上传的文件
        fileQueue.shift();
        checkFileQueue();

        console.error(error);
    };

    /**
     * 导入成功后的回调函数，处理成功后回调的场景
     * @param docGuid 文档 ID
     * @param parentGuid 文档的父 ID
     */
    const handleSuccessCallBack = (docGuid?: string, parentGuid?: string) => {
        // 若在首页，导入完成，更新最近浏览列表
        if (location.pathname === '/') {
            refreshRecentList();
        }
        // 若在文档域页面，更新目录树（暂时先这么判断，后续看看有没更合适的方式）
        if (location.pathname.includes('/knowledge')) {
            // 新目录树逻辑
            const {value: treeData} = store.getSidebarTreeObj();
            if (!treeData?.length) {
                return;
            }
            const repo = store.getCurRepository();
            if (parentGuid) {
                const parentNode = getNodeById(treeData, parentGuid);
                if (parentNode) {
                    repo && updateChildNodes(repo, parentGuid);
                }
            } else {
                repo && updateChildNodes(repo, '');
            }
        }
    };

    /**
     * 获取文件导入进度（Excel）
     * @param {FileItem} file 文件信息
     */
    const getFileProgress = async (file: FileItem) => {
        try {
            const curRepository = store.getCurRepository();
            const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);
            const result = await ksServer.getImportProgress(file.docGuid as string, file.options?.taskId || '');
            const {data: {progress}} = result;

            if (progress === 100) {
                clearTimer();

                file.status = FileStatus.Success;
                store.setImportFile(file);
                handleSuccessCallBack(file.docGuid, file.options?.parentGuid);

                // 清除队列头，检查是否还有未上传的文件
                fileQueue.shift();
                checkFileQueue();
            } else {
                file.progress = Math.round(50 + progress * 0.5);
                store.setImportFile(file);
            }
        } catch (error) {
            clearTimer();
            handleFileError(file, error);
        }
    };

    /**
    * 获取文件导入进度（PPT）- 客户端导入场景使用
    * @param {FileItem} file 文件信息
    */
    const getPPTProgress = async (file: FileItem) => {
        try {
            const curRepository = store.getCurRepository();
            const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);
            const {progress} = await ksServer.getImportPPTProgress(file.docGuid as string);

            if (progress === 100) {
                clearTimer();

                file.status = FileStatus.Success;
                store.setImportFile(file);
                handleSuccessCallBack(file.docGuid);

                // 清除队列头，检查是否还有未上传的文件
                fileQueue.shift();
                checkFileQueue();
            } else {
                file.progress = progress;
                store.setImportFile(file);
            }
        } catch (error) {
            clearTimer();
            handleFileError(file, error);
        }
    };

    /**
     * 获取文件导入进度（Word）
     * @param {FileItem} file 文件信息
     */
    const getWordProgress = async (file: FileItem) => {
        try {
            const curRepository = store.getCurRepository();
            const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);
            const result = await ksServer.getImportWordProgress(file.options?.taskId || '');
            const {progress, status, docGuid} = result;

            if (progress === 100) {
                clearTimer();

                file.docGuid = docGuid;
                file.status = FileStatus.Success;
                store.setImportFile(file);
                handleSuccessCallBack(file.docGuid, file.options?.parentGuid);

                // 清除队列头，检查是否还有未上传的文件
                fileQueue.shift();
                checkFileQueue();
            } else {
                file.progress = Math.round(50 + progress * 0.5);
                store.setImportFile(file);
            }

            // 1-导入开始、2-导入中、3-导入成功、其他-失败
            if (![1, 2, 3].includes(status)) {
                throw Error('word 导入失败');
            }
        } catch (error) {
            clearTimer();
            handleFileError(file, error);
        }
    };

    /**
     * 调用 PPT 上传接口
     * @param file 文件信息
     */
    const importPPT = async (file: FileItem) => {
        try {
            if (file.options?.fid) { // 来自客户端导入的文件
                file.status = FileStatus.Progress;
                store.setImportFile(file);

                const ksServer = await sdk.getKsServer(sdk.as.userRepository);
                const result = await ksServer.importPPTFromClient({
                    uid: sdk.as.user?.rlId || '',
                    ...file.options,
                });

                const {docGuid, docName} = result;

                file.docGuid = docGuid;
                file.options.docName = docName;
                store.setImportFile(file);

                // 轮询导入进度
                getPPTProgress(file);
                timer = window.setInterval(() => {
                    getPPTProgress(file);
                }, GetProgressTimeSpace);
            } else { // 知识库 web 端正常导入
                file.status = FileStatus.Progress;
                store.setImportFile(file);

                const curRepository = store.getCurRepository();
                const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);

                // 动态获取分片上传方法
                const {uploadPartialFile, getMd5ByFile} = await getUploadPartialFileFunc();
                const md5 = await getMd5ByFile(file.target); // 存储文件的 md5
                const key = `ppt/${uuidv1()}`; // 自定义存储在 bos 上的文件 key 值

                // 获取 bos 上传 STS 临时认证票据
                const STSData = await ksServer.getBosSTSData(key, file.options?.parentGuid);
                const {accessKeyId, bucket, secretAccessKey, endpoint, sessionToken} = STSData;

                const bosConfig = {
                    endpoint,
                    sessionToken,
                    credentials: {
                        ak: accessKeyId,
                        sk: secretAccessKey,
                    },
                };

                // 文件上传 bos
                const bosResult = await uploadPartialFile(
                    file.target, bosConfig, bucket,
                    (progress: number) => { // 更新上传进度
                        file.progress = Math.round(progress * 100);
                        store.setImportFile(file);
                    },
                    () => {
                        return key; // 自定义存储在 bos 上的文件 key 值
                    },
                    false
                );

                // 上传 bos 成功，通知后端获取演示文稿 docGuid
                const result = await ksServer.importPPT(
                    bosResult.body.key,
                    file.target.name,
                    md5,
                    file.options?.parentGuid as string
                );
                const {docGuid} = result;

                file.docGuid = docGuid;
                file.status = FileStatus.Success;
                store.setImportFile(file);
                handleSuccessCallBack(file.docGuid, file.options?.parentGuid);

                // 清除队列头，检查是否还有未上传的文件
                fileQueue.shift();
                checkFileQueue();
            }
        } catch (error: any) {
            handleFileError(file, error);
        }
    };

    /**
     * 调用 Excel 上传接口（包括客户端导入、web 端导入两种情况）
     * @param file 文件信息
     */
    const importExcel = async (file: FileItem) => {
        try {
            if (file.options?.fid) { // 来自客户端导入的文件
                file.status = FileStatus.Progress;
                store.setImportFile(file);

                const ksServer = await sdk.getKsServer(sdk.as.userRepository);
                const result = await ksServer.importExcelFromClient({
                    uid: sdk.as.user?.rlId || '',
                    ...file.options,
                });
                const {docGuid, docName, data: {taskId}} = result;

                file.docGuid = docGuid;
                file.options.docName = docName;
                file.options.taskId = taskId;
                store.setImportFile(file);

                // 轮询导入进度
                getFileProgress(file);
                timer = window.setInterval(() => {
                    getFileProgress(file);
                }, GetProgressTimeSpace);
            } else { // 知识库 web 端正常导入
                file.status = FileStatus.Progress;
                store.setImportFile(file);

                const curRepository = store.getCurRepository();
                const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);

                const key = `excel/${uuidv1()}`; // 自定义存储在 bos 上的文件 key 值

                // 获取 bos 上传 STS 临时认证票据
                const STSData = await ksServer.getBosSTSData(key, file.options?.parentGuid);
                const {accessKeyId, bucket, secretAccessKey, endpoint, sessionToken} = STSData;

                const bosConfig = {
                    endpoint,
                    sessionToken,
                    credentials: {
                        ak: accessKeyId,
                        sk: secretAccessKey,
                    },
                };

                // 动态获取分片上传方法
                const {uploadPartialFile} = await getUploadPartialFileFunc();

                // 文件上传 bos
                const bosResult = await uploadPartialFile(
                    file.target, bosConfig, bucket,
                    (progress: number) => { // 更新上传进度
                        file.progress = Math.round(progress * 100 * 0.5);
                        store.setImportFile(file);
                    },
                    () => {
                        return key; // 自定义存储在 bos 上的文件 key 值
                    },
                    false
                );

                // 知识库发起导入，接口返回成功，表示文件已成功上传后端，开始下个步骤（导入石墨）
                const result = await ksServer.importExcel(
                    bosResult.body.key,
                    file.target.name,
                    file.options?.parentGuid as string
                );
                const {docGuid, data: {taskId}} = result;
                file.docGuid = docGuid;
                file.options && (file.options.taskId = taskId);
                store.setImportFile(file);

                // 轮询导入进度
                getFileProgress(file);
                timer = window.setInterval(() => {
                    getFileProgress(file);
                }, GetProgressTimeSpace);
            }
        } catch (error: any) {
            handleFileError(file, error);
        }
    };

    /**
     * 调用 Word 上传接口
     * @param file 文件信息
     */
    const importWord = async (file: FileItem) => {
        try {
            file.status = FileStatus.Progress;
            store.setImportFile(file);

            const curRepository = store.getCurRepository();
            const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);
            const taskId = uuidv1();
            await ksServer.importWord(
                file.target,
                file.options?.parentGuid as string,
                taskId,
                (progress: number) => { // 更新上传进度
                    file.progress = Math.round(progress * 0.5);
                    store.setImportFile(file);
                }
            );

            file.options && (file.options.taskId = taskId);
            store.setImportFile(file);

            // 轮询导入进度
            getWordProgress(file);
            timer = window.setInterval(() => {
                getWordProgress(file);
            }, GetProgressTimeSpace);
        } catch (error: any) {
            handleFileError(file, error);
        }
    };

    /**
     * 调用 PDF 上传接口
     * @param file 文件信息
     */
    const importPDF = async (file: FileItem) => {
        try {
            file.status = FileStatus.Progress;
            store.setImportFile(file);

            const curRepository = store.getCurRepository();
            const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);
            const {docGuid} = await sdk.as.getDocGuid();

            // 动态获取分片上传方法
            const {uploadPartialFile, getMd5ByFile} = await getUploadPartialFileFunc();
            // const md5 = await getMd5ByFile(file.target); // 存储文件的 md5

            const STSData = await ksServer.getAttachBosSTSData(docGuid);
            const {accessKeyId, bucket, secretAccessKey, endpoint, sessionToken, objectKey} = STSData;

            const bosConfig = {
                endpoint,
                sessionToken,
                credentials: {
                    ak: accessKeyId,
                    sk: secretAccessKey,
                },
            };

            // 文件上传 bos
            const bosResult = await uploadPartialFile(
                file.target, bosConfig, bucket,
                (progress: number) => { // 更新上传进度
                    file.progress = Math.round(progress * 100);
                    store.setImportFile(file);
                },
                objectKey,
                false
            );

            // 保存附件信息
            const saveResult = await ksServer.saveAttachInfo({
                docGuid,
                bucket,
                fileName: file.target.name,
                fileSize: file.target.size,
                object: bosResult.body.key,
            });
            const {attachId} = saveResult;

            // 导入 PDF 创建文档
            await ksServer.importPDF({
                fileName: file.target.name,
                fileSize: file.target.size,
                parentGuid: file.options?.parentGuid as string,
                docGuid,
                fileId: attachId,
            });

            file.docGuid = docGuid;
            file.status = FileStatus.Success;
            store.setImportFile(file);
            handleSuccessCallBack(file.docGuid, file.options?.parentGuid);

            // 清除队列头，检查是否还有未上传的文件
            fileQueue.shift();
            checkFileQueue();
        } catch (error: any) {
            handleFileError(file, error);
        }
    };

    /**
     * 调用 Excel(数据表) 上传接口
     * @param file 文件信息
     */
    const importExcelDst = async (file: FileItem) => {
        try {
            file.status = FileStatus.Progress;
            store.setImportFile(file);

            const curRepository = store.getCurRepository();
            const ksServer = await sdk.getKsServer(curRepository || sdk.as.userRepository);
            const result = await ksServer.importExcelDst(
                file.target,
                file.options?.parentGuid as string,
                (progress: number) => { // 更新上传进度
                    file.progress = Math.round(progress * 0.5);
                    store.setImportFile(file);
                }
            );
            const {docGuid} = result;

            file.docGuid = docGuid;
            file.status = FileStatus.Success;
            file.progress = 100;
            store.setImportFile(file);
            handleSuccessCallBack(file.docGuid, file.options?.parentGuid);

            // 清除队列头，检查是否还有未上传的文件
            fileQueue.shift();
            checkFileQueue();
        } catch (error: any) {
            handleFileError(file, error);
        }
    };

    /**
    * 队列头文件执行上传操作
    */
    const importQueueHeadFile = () => {
        switch (fileQueue[0].fileType) {
            case FileType.Excel:
                importExcel(fileQueue[0]);
                break;
            case FileType.PPT:
                importPPT(fileQueue[0]);
                break;
            case FileType.Word:
                importWord(fileQueue[0]);
                break;
            case FileType.PDF:
                importPDF(fileQueue[0]);
                break;
            case FileType.ExcelDst:
                importExcelDst(fileQueue[0]);
                break;
            default:
                break;
        }
    };

    /**
     * 导入文件处理
     * @param files 用户选择的本地文件信息
     */
    const uploadFile = (files: File[], uploadInputOptions: UploadInputOptions) => {
        const fileArr: File[] = [].slice.call(files);

        // 文件校验
        const MaxSize = UPLOAD_MAXSZIE[uploadInputOptions.fileType]; // 文件大小上限
        for (const file of fileArr) {
            if (file.size > MaxSize) {
                toast({
                    content: FILE_MAX_TIP_MAP[uploadInputOptions.fileType],
                    type: ToastType.Error,
                    duration: 3000,
                });
                return;
            }
        }

        // 初始化文件对象列表
        const {fileType, curDocument, source} = uploadInputOptions;
        const initFiles: FileItem[] = fileArr.map(file => {
            let parentGuid = '';

            // 若在文档域页面
            if (location.pathname.includes('/knowledge')) {
                const treeObj = store.getSidebarTreeObj();
                const selectedKeys = treeObj?.getSelectedKeys() || [];

                if (
                    (source === 'header'
                        && curDocument
                        && selectedKeys[0] === curDocument?.docGuid) // 来源是顶栏导入 & 判断目录树有选中的文档
                    || (source === 'sidebar' && curDocument) // 来源是侧边栏目录树导入 & 若存在文件对象，代表不是根目录导入
                ) {
                    parentGuid = curDocument.docGuid;
                }
            }

            return {
                status: FileStatus.Init,
                target: file,
                progress: 0,
                fileType,
                options: {
                    parentGuid,
                },
            };
        });

        store.addImportFiles(initFiles);

        // 推入文件队列等待导入
        if (fileQueue.length) { // 文件队列不为空，则仅将文件推入文件队列，不执行上传
            initFiles.forEach(file => {
                fileQueue.push(file);
            });
            return;
        } else {
            initFiles.forEach(file => {
                fileQueue.push(file);
            });
        }

        importQueueHeadFile();
    };

    /**
     * 导入 Excel 文件处理（来自客户端）
     * @param options 额外文件信息
     */
    const uploadFileFromClient = (options: ClientTransformFileData, fileType: FileType) => {
        const initFile: FileItem = {
            status: FileStatus.Init,
            target: {name: ''} as File,
            progress: 0,
            fileType,
            options,
        };

        store.addImportFiles([initFile]);
        if (fileQueue.length) { // 文件队列不为空，则仅将文件推入文件队列，不执行上传
            fileQueue.push(initFile);
            return;
        } else {
            fileQueue.push(initFile);
        }

        importQueueHeadFile();
    };

    const handleRetry = (file: FileItem) => {
        file.status = FileStatus.Init;
        file.progress = 0;
        store.setImportFile(file);

        if (fileQueue.length) { // 文件队列不为空，则仅将文件推入文件队列，不执行上传
            fileQueue.push(file);
            return;
        } else {
            fileQueue.push(file);
        }

        importQueueHeadFile();
    };

    const handleRetryAll = (files: FileItem[]) => {
        files.forEach(file => {
            handleRetry(file);
        });
    };

    const handleCancelAll = () => {
        if (fileQueue.length) {
            // 给已清除的文件打上标记
            fileQueue.forEach(file => {
                file.isClear = true;
            });
        }
        fileQueue.length = 0;
    };

    return {
        uploadFile,
        uploadFileFromClient,
        handleRetry,
        handleRetryAll,
        handleCancelAll,
    };
};
